import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, CheckCircle, AlertTriangle, Loader2, ArrowLeft, RefreshCcw, FileSpreadsheet } from 'lucide-react';
import api from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

export default function ImportManager() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [platform, setPlatform] = useState('uber');
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setError('O motor de auditoria exige ficheiros no formato .csv');
      setFile(null);
      return;
    }
    setFile(selectedFile);
    setError(null);
    setSummary(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setSummary(null);

    const formData = new FormData();
    formData.append('csv', file);
    formData.append('platform', platform);

    try {
      const { data } = await api.post('/finance/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.success) {
        setSummary(data.data || data);
        toast.success('Processamento Concluído', { description: `${data.data?.processedCount || 0} viagens auditadas.` });
        setFile(null);
      } else {
        throw new Error(data.error || 'Erro no processamento');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Erro na integridade do CSV. Verifique os nomes dos motoristas.';
      setError(errorMsg);
      toast.error('Falha na Importação', { description: errorMsg });
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setSummary(null);
    setFile(null);
    setError(null);
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-2 md:p-4">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate('/finance/wallets')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Auditoria de Faturamento</h1>
          <p className="text-sm text-muted-foreground">Importação e Cálculo de Split Dinâmico</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        <Card className="lg:col-span-4">
          <CardHeader>
            <CardTitle className="text-sm uppercase tracking-wide text-muted-foreground">1. Origem dos Dados</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {['uber', 'bolt'].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => { setPlatform(p); setSummary(null); }}
                className={`flex w-full items-center justify-between rounded-lg border-2 p-4 text-sm font-semibold transition-all ${
                  platform === p
                    ? 'border-primary bg-primary/5 text-primary'
                    : 'border-border text-muted-foreground hover:border-primary/50'
                }`}
              >
                <span className="capitalize">{p}</span>
                {platform === p && <CheckCircle className="h-5 w-5" />}
              </button>
            ))}
          </CardContent>
        </Card>

        <div className="lg:col-span-8 space-y-4">
          {!summary ? (
            <Card className="flex min-h-[360px] flex-col items-center justify-center border-2 border-dashed p-8">
              <div className={`mb-4 flex h-20 w-20 items-center justify-center rounded-2xl transition-all ${file ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                <FileSpreadsheet className="h-10 w-10" />
              </div>
              <h3 className="text-lg font-semibold">
                {file ? file.name : 'Selecionar Relatório CSV'}
              </h3>
              <p className="mb-6 text-center text-sm text-muted-foreground">
                Formato aceito: CSV {platform === 'uber' ? '(vírgula)' : '(ponto e vírgula)'} · Máx. 15MB
              </p>

              <input
                type="file"
                id="csvUpload"
                className="sr-only"
                accept=".csv"
                onChange={handleFileChange}
              />

              {!uploading && (
                <label htmlFor="csvUpload">
                  <Button asChild variant="outline">
                    <span>Explorar Ficheiros</span>
                  </Button>
                </label>
              )}

              {uploading && (
                <div className="flex flex-col items-center gap-2 text-primary">
                  <Loader2 className="h-8 w-8 animate-spin" />
                  <p className="text-xs font-semibold uppercase">A sincronizar com o ledger...</p>
                </div>
              )}
            </Card>
          ) : (
            <Card className="border-2 border-emerald-600 bg-emerald-600 text-emerald-50">
              <CardContent className="flex flex-col items-center py-10 text-center">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/20">
                  <CheckCircle className="h-8 w-8" />
                </div>
                <h2 className="text-2xl font-bold">Relatório Consolidado</h2>
                <div className="mt-6 grid w-full max-w-sm grid-cols-2 gap-4">
                  <div className="rounded-xl border border-white/10 bg-white/10 p-4">
                    <p className="text-xs font-semibold uppercase opacity-70">Viagens</p>
                    <p className="text-3xl font-bold">{summary.processedCount || 0}</p>
                  </div>
                  <div className="rounded-xl border border-white/10 bg-white/10 p-4">
                    <p className="text-xs font-semibold uppercase opacity-70">Receita Bruta</p>
                    <p className="text-3xl font-bold">{summary.totalRevenue || 0}€</p>
                  </div>
                </div>
                <Button onClick={reset} variant="secondary" className="mt-8">
                  <RefreshCcw className="mr-2 h-4 w-4" /> Nova Importação
                </Button>
              </CardContent>
            </Card>
          )}

          {!summary && (
            <>
              {error && (
                <div className="flex items-center gap-3 rounded-lg border border-destructive/20 bg-destructive/10 p-4 text-destructive">
                  <AlertTriangle className="h-5 w-5 shrink-0" />
                  <span className="text-sm font-semibold">{error}</span>
                </div>
              )}
              <Button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="w-full"
                size="lg"
              >
                {uploading ? (
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                ) : (
                  <Upload className="mr-2 h-5 w-5" />
                )}
                {uploading ? 'A sincronizar com o Ledger...' : 'Iniciar Auditoria de Faturamento'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
